import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Accordion, Alert } from 'react-bootstrap-5';
import {
  createVaultPromises, GATE_ROLES,
  handleNewAccessLevels,
  setupInitialCaSettings,
  setupInitialGates,
  vaultRoleKey
} from './helper';
import { GATE_PROPS } from './role_access_settings';
import Snackbar from 'shared/snackbar';
import { CA_DATA_SHAPE } from './confidentiality_agreement_form';
import SaveSettingsButton from './save_settings_button';
import withDragDropContext from 'helpers/react_dnd/with_drag_drop_context';
import VaultGate from './vault_gate';

function DocumentAccessSettings({ allowDigitalSignPdfCas, allowMultipleCasPerRole, bulkModifyPath, hasLeads, vaults }) {
  const [savedGates, setSavedGates] = useState(setupInitialGates(vaults));
  const [gates, setGates] = useState(setupInitialGates(vaults));
  const [caSettings, setCaSettings] = useState(setupInitialCaSettings(vaults));
  const [isSaving, setIsSaving] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [errors, setErrors] = useState([]);

  function handleCaFileUploaded(uploadData, caDataKey) {
    setCaSettings(caData => ({
      ...caData,
      [caDataKey]: {
        ...caData[caDataKey],
        ...uploadData
      }
    }));
  }

  function handleSimpleCaSettingsChange(field, newValue, caDataKey) {
    setCaSettings(caData => ({
      ...caData,
      [caDataKey]: {
        ...caData[caDataKey],
        [field]: newValue
      }
    }));
  }

  function saveAccessSettings() {
    setIsSaving(true);
    setErrors([]);

    Promise.allSettled(createVaultPromises(vaults, gates, caSettings, bulkModifyPath))
      .then((results) => {
        const failureReasons = results.reduce((accumulator, result) => {
          if (result.status === 'rejected') accumulator.push(...result.reason);
          return accumulator;
        }, []);

        setErrors(failureReasons);
        if (failureReasons.length === 0) {
          setShowSnackbar(true);
          setSavedGates(gates);
          const updatedVaultData = results.find(result => result.value?.hasOwnProperty('vaultData'));
          if (updatedVaultData) dispatchVaultData(updatedVaultData);
        }
      }).finally(() => setIsSaving(false));
  }

  function dispatchVaultData(updatedVaultData) {
    const event = new CustomEvent('updateVaultData', { detail: updatedVaultData.value.vaultData });
    document.dispatchEvent(event);
  }

  function handleGatesChange(modifiedGates, vaultId, role) {
    const gateKey = vaultRoleKey(vaultId, role);
    setGates({ ...gates, [gateKey]: modifiedGates });
  }

  useEffect(() => {
    document.addEventListener('accessLevelsChanged', ({ detail: { accessLevels, vaultId } }) => {
      setGates(prevGates => handleNewAccessLevels(prevGates, accessLevels, vaultId));
    });
  }, []);

  return (
    <React.Fragment>
      <Snackbar
        show={showSnackbar}
        text="Saved Access Settings"
        variant="success"
        onClose={() => setShowSnackbar(false)}
      />

      <SaveSettingsButton
        gates={gates}
        hasLeads={hasLeads}
        isSaving={isSaving}
        savedGates={savedGates}
        onClick={saveAccessSettings}
      />

      {errors.length > 0 && (
        <Alert className="mt-4" variant="danger">
          <div className="d-flex">
            <div className="ms-4 mt-1">
              <div><strong>The following errors occurred when trying to save this form. Please fix and re-submit.</strong></div>
              <ul className="mb-0">
                {errors.map(error => (
                  <li key={error}>{error}</li>
                ))}
              </ul>
            </div>
          </div>
        </Alert>
      )}

      <Accordion alwaysOpen className="border rounded inverse overflow-hidden mb-6" defaultActiveKey={vaults.map(v => v.id)}>
        {vaults.map(vault => (
          <Accordion.Item className="doc-center-vault-settings-accordion" eventKey={vault.id} key={vault.id}>
            <Accordion.Header but className={`bg-light ${vaults.length === 1 ? 'd-none' : ''}`}>
              <div className="accordion-head-text fs-large fw-bold">
                <span className="text-capitalize">{vault.saleOrLease}</span> Access
              </div>
            </Accordion.Header>
            <Accordion.Body className="bg-white">
              {GATE_ROLES.map(role => (
                <VaultGate
                  allowDigitalSignPdfCas={allowDigitalSignPdfCas}
                  allowMultipleCasPerRole={allowMultipleCasPerRole}
                  caSettings={caSettings}
                  gates={gates}
                  key={role.type}
                  role={role}
                  vault={vault}
                  onCaFileUpload={handleCaFileUploaded}
                  onGatesChange={handleGatesChange}
                  onSimpleCaSettingsChange={handleSimpleCaSettingsChange}
                />
              ))}
            </Accordion.Body>
          </Accordion.Item>
        ))}
      </Accordion>
    </React.Fragment>
  );
}

DocumentAccessSettings.propTypes = {
  allowDigitalSignPdfCas: PropTypes.bool.isRequired,
  allowMultipleCasPerRole: PropTypes.bool.isRequired,
  bulkModifyPath: PropTypes.string.isRequired,
  hasLeads: PropTypes.bool.isRequired,
  vaults: PropTypes.arrayOf(PropTypes.shape({
    brokerCaData: PropTypes.shape(CA_DATA_SHAPE),
    caOptions: PropTypes.arrayOf(PropTypes.array).isRequired,
    gates: GATE_PROPS,
    id: PropTypes.number.isRequired,
    ownerCaData: PropTypes.shape(CA_DATA_SHAPE),
    saleOrLease: PropTypes.oneOf(['sale', 'lease']),
    standardBrokerCa: PropTypes.string.isRequired,
    standardOwnerCa: PropTypes.string.isRequired,
    startingAccessLevels: PropTypes.arrayOf(PropTypes.string).isRequired
  })),
};

export default withDragDropContext(DocumentAccessSettings);
